import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CATEGORIES, PLATFORMS, CONTENT_TYPES, CAMPAIGN_TYPES, COUNTRIES, LANGUAGES, CURRENCY_SYMBOLS } from "@/lib/constants";
import { toast } from "sonner";
import { Megaphone, ArrowLeft } from "lucide-react";

export default function PostCampaign() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const sym = CURRENCY_SYMBOLS[user.currency] || "";
  const [f, setF] = useState({
    title: "", objective: "", product: "", content_type: "", platforms: [],
    target_audience: "", location: "", deliverables: "", timeline: "", budget: "",
    category: "", campaign_type: "", special_instructions: "",
    country: "", city: "", languages: [],
    compensation_type: "amount", hamper_value: "", hamper_items: "", hamper_influencers_required: "", disclaimer: false,
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const togglePlatform = (p) => setF((s) => ({ ...s, platforms: s.platforms.includes(p) ? s.platforms.filter((x) => x !== p) : [...s.platforms, p] }));
  const toggleLanguage = (l) => setF((s) => ({ ...s, languages: s.languages.includes(l) ? s.languages.filter((x) => x !== l) : [...s.languages, l] }));

  const isHamper = f.compensation_type === "gift_hamper";
  const payOk = isHamper
    ? (Number(f.hamper_value) > 0 && f.hamper_items.trim() && Number(f.hamper_influencers_required) > 0 && f.disclaimer)
    : Number(f.budget) > 0;
  const canSubmit = f.title.trim() && f.objective.trim() && f.deliverables.trim() && payOk;

  const submit = async () => {
    if (!canSubmit) { toast.error(isHamper ? "Fill hamper value, items, influencers needed and accept the disclaimer." : "Fill title, objective, deliverables and a valid budget."); return; }
    setSaving(true);
    try {
      const payload = {
        ...f,
        budget: Number(f.budget) || 0,
        hamper_value: f.hamper_value ? Number(f.hamper_value) : null,
        hamper_influencers_required: f.hamper_influencers_required ? Number(f.hamper_influencers_required) : null,
        sample_disclaimer_accepted: f.disclaimer,
      };
      delete payload.disclaimer;
      const res = await api.post("/campaigns", payload);
      toast.success("Campaign posted! Creators can now bid.");
      navigate(`/campaigns/${res.data.campaign_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post campaign.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-btn"><ArrowLeft className="h-4 w-4" /> Back</button>
        <div className="flex items-center gap-3 mb-8">
          <div className="h-12 w-12 rounded-xl bg-neon-lime/10 border border-neon-lime/20 flex items-center justify-center"><Megaphone className="h-6 w-6 text-neon-lime" /></div>
          <div>
            <h1 className="font-heading font-black text-3xl tracking-tight">Post a campaign</h1>
            <p className="text-muted-foreground text-sm">Describe your requirement and let creators bid.</p>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-border bg-surface p-7 space-y-6">
          <div>
            <Label className="mb-2 block">Campaign title *</Label>
            <Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="campaign-title-input" placeholder="e.g. Summer skincare reel collaboration" className="bg-background border-border h-11" />
          </div>
          <div>
            <Label className="mb-2 block">Objective *</Label>
            <Textarea value={f.objective} onChange={(e) => set("objective", e.target.value)} data-testid="campaign-objective-input" placeholder="What do you want to achieve? e.g. brand awareness, product promotion…" className="bg-background border-border min-h-[80px]" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="mb-2 block">Product / service</Label>
              <Input value={f.product} onChange={(e) => set("product", e.target.value)} data-testid="campaign-product-input" placeholder="e.g. Vitamin C serum" className="bg-background border-border h-11" />
            </div>
            <div>
              <Label className="mb-2 block">Category</Label>
              <Select value={f.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="campaign-category-select" className="bg-background border-border h-11"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="mb-2 block">Campaign type</Label>
              <Select value={f.campaign_type} onValueChange={(v) => set("campaign_type", v)}>
                <SelectTrigger data-testid="campaign-type-select" className="bg-background border-border h-11"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{CAMPAIGN_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">Primary content type</Label>
              <Select value={f.content_type} onValueChange={(v) => set("content_type", v)}>
                <SelectTrigger data-testid="campaign-contenttype-select" className="bg-background border-border h-11"><SelectValue placeholder="Select content" /></SelectTrigger>
                <SelectContent>{CONTENT_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="mb-3 block">Platforms required</Label>
            <div className="flex flex-wrap gap-2" data-testid="campaign-platforms">
              {PLATFORMS.map((p) => {
                const active = f.platforms.includes(p);
                return <button type="button" key={p} onClick={() => togglePlatform(p)} data-testid={`platform-${p.toLowerCase()}`}
                  className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${active ? "bg-neon-lime text-background border-neon-lime font-semibold" : "border-border bg-background text-muted-foreground hover:text-foreground"}`}>{p}</button>;
              })}
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Deliverables *</Label>
            <Textarea value={f.deliverables} onChange={(e) => set("deliverables", e.target.value)} data-testid="campaign-deliverables-input" placeholder="e.g. 1 Reel + 3 Stories, or 1 YouTube video" className="bg-background border-border min-h-[70px]" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="mb-2 block">Target audience</Label>
              <Input value={f.target_audience} onChange={(e) => set("target_audience", e.target.value)} data-testid="campaign-audience-input" placeholder="e.g. Women 18-34, metros" className="bg-background border-border h-11" />
            </div>
            <div>
              <Label className="mb-2 block">Location (short)</Label>
              <Input value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="campaign-location-input" placeholder="e.g. India / Mumbai" className="bg-background border-border h-11" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="mb-2 block">Country</Label>
              <Select value={f.country} onValueChange={(v) => set("country", v)}>
                <SelectTrigger data-testid="campaign-country-select" className="bg-background border-border h-11"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">City</Label>
              <Input value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="campaign-city-input" placeholder="e.g. Mumbai" className="bg-background border-border h-11" />
            </div>
          </div>
          <div>
            <Label className="mb-3 block">Content language(s)</Label>
            <div className="flex flex-wrap gap-2" data-testid="campaign-languages">
              {LANGUAGES.map((l) => {
                const active = f.languages.includes(l);
                return <button type="button" key={l} onClick={() => toggleLanguage(l)} data-testid={`language-${l.toLowerCase()}`}
                  className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${active ? "bg-neon-lime text-background border-neon-lime font-semibold" : "border-border bg-background text-muted-foreground hover:text-foreground"}`}>{l}</button>;
              })}
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Timeline</Label>
            <Input value={f.timeline} onChange={(e) => set("timeline", e.target.value)} data-testid="campaign-timeline-input" placeholder="e.g. Within 2 weeks" className="bg-background border-border h-11" />
          </div>

          <div className="rounded-2xl border border-border bg-background p-5 space-y-4">
            <div>
              <Label className="mb-2 block">How will you compensate creators? *</Label>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => set("compensation_type", "amount")} data-testid="comp-type-amount"
                  className={`rounded-xl border p-3 text-left transition-colors ${!isHamper ? "border-neon-lime bg-neon-lime/10 ring-2 ring-neon-lime" : "border-border hover:bg-surfaceHover"}`}>
                  <p className="font-heading font-bold text-sm">💰 Pay an amount</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Cash budget creators bid against</p>
                </button>
                <button type="button" onClick={() => set("compensation_type", "gift_hamper")} data-testid="comp-type-hamper"
                  className={`rounded-xl border p-3 text-left transition-colors ${isHamper ? "border-neon-lime bg-neon-lime/10 ring-2 ring-neon-lime" : "border-border hover:bg-surfaceHover"}`}>
                  <p className="font-heading font-bold text-sm">🎁 Gift hamper</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Products instead of cash</p>
                </button>
              </div>
            </div>

            {!isHamper ? (
              <div>
                <Label className="mb-2 block">Maximum budget * ({sym}{user.currency})</Label>
                <Input type="number" value={f.budget} onChange={(e) => set("budget", e.target.value)} data-testid="campaign-budget-input" placeholder="e.g. 25000" className="bg-surface border-border h-11" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="mb-2 block">Hamper value * ({sym}{user.currency})</Label>
                    <Input type="number" value={f.hamper_value} onChange={(e) => set("hamper_value", e.target.value)} data-testid="campaign-hamper-value-input" placeholder="e.g. 5000" className="bg-surface border-border h-11" />
                  </div>
                  <div>
                    <Label className="mb-2 block">Influencers needed *</Label>
                    <Input type="number" value={f.hamper_influencers_required} onChange={(e) => set("hamper_influencers_required", e.target.value)} data-testid="campaign-hamper-count-input" placeholder="e.g. 10" className="bg-surface border-border h-11" />
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Items included in the hamper *</Label>
                  <Textarea value={f.hamper_items} onChange={(e) => set("hamper_items", e.target.value)} data-testid="campaign-hamper-items-input" placeholder="e.g. Vitamin C serum, moisturizer, face wash, tote bag" className="bg-surface border-border min-h-[70px]" />
                </div>
                <label className="flex items-start gap-3 rounded-xl border border-brand-orange/40 bg-brand-orange/5 p-3 cursor-pointer" data-testid="campaign-disclaimer-label">
                  <input type="checkbox" checked={f.disclaimer} onChange={(e) => set("disclaimer", e.target.checked)} data-testid="campaign-disclaimer-checkbox" className="mt-0.5 h-4 w-4 accent-neon-lime shrink-0" />
                  <span className="text-xs text-muted-foreground">To approve this offer, you agree to share <span className="font-semibold text-foreground">3 gift hampers to InfluencerCollab</span> to verify and approve as samples — at no cost to you.</span>
                </label>
              </div>
            )}
          </div>
          <div>
            <Label className="mb-2 block">Special instructions</Label>
            <Textarea value={f.special_instructions} onChange={(e) => set("special_instructions", e.target.value)} data-testid="campaign-instructions-input" placeholder="Anything else creators should know…" className="bg-background border-border min-h-[70px]" />
          </div>

          <Button onClick={submit} disabled={!canSubmit || saving} data-testid="submit-campaign-btn" className="w-full h-12 rounded-full bg-neon-lime text-background hover:bg-neon-lime/90 font-bold text-base">
            {saving ? "Posting…" : "Post Campaign"}
          </Button>
        </motion.div>
      </div>
    </AppShell>
  );
}
